import React from "react";
import { useTranslation } from "react-i18next";
import { Panel } from './Panel';
import { ButtonRowSlotSettings } from './blocks/button-row-slot';
import { findBlock } from '../domain/find';
import { translateButtonRowSlotLabel } from '../domain/labels';
import { selectDocument } from '../domain/selectors';
import { useEditorActions, useEditorStore } from '../domain/redux/hooks';
import type { ButtonRowBlock } from '../domain/types';
import { BUTTON_ROW_SLOT_SETTINGS_DEFAULT_EXPANDED } from './settingsPanelDefaults';

type ButtonRowSlotSettingsPanelProps = {
  rowId: string;
  columnId: string;
  blockId: string;
  slotIndex: number;
};

export function ButtonRowSlotSettingsPanel({
  rowId,
  columnId,
  blockId,
  slotIndex,
}: ButtonRowSlotSettingsPanelProps) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const { updateBlock } = useEditorActions();
  const block = findBlock(document, rowId, columnId, blockId);

  if (!block || block.type !== 'buttonRow') {
    return null;
  }

  const slot = block.buttons[slotIndex];
  if (!slot) {
    return null;
  }

  const patchSlot = (slotPatch: Partial<ButtonRowBlock['buttons'][number]>) => {
    const buttons = [...block.buttons];
    const current = buttons[slotIndex];
    if (!current) {
      return;
    }
    buttons[slotIndex] = { ...current, ...slotPatch };
    updateBlock(rowId, columnId, blockId, {
      buttons: buttons as ButtonRowBlock['buttons'],
    });
  };

  return (
    <Panel
      title={translateButtonRowSlotLabel(t, slotIndex)}
      defaultExpandedSectionIds={BUTTON_ROW_SLOT_SETTINGS_DEFAULT_EXPANDED}
    >
      <ButtonRowSlotSettings
        slot={slot}
        slotIndex={slotIndex}
        patch={patchSlot}
      />
    </Panel>
  );
}
